import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../../app/hooks";
import { MessageAttachment } from "../models/MessageAttachment";
import { MessagesChannel } from "../models/MessagesChannel";
import { AttachmentError, beginAttachment, completeAttachment } from "../slice-extensions/attachments-slice-extension";
import { contentTypeOf, formatBytes, kindOf, maxBytesOf } from "../utils/attachmentRules";
import { prepareImage, preparedFileName } from "../utils/prepareImage";
import { putWithProgress } from "../utils/putWithProgress";

/** An attachment in the composer: uploading (progress 0–1) or done with its server row. */
export interface PendingAttachment {
  localId: string;
  fileName: string;
  progress: number;
  attachment: MessageAttachment | null;
}

let nextLocalId = 0;

/**
 * Uploads chosen files straight to the bucket: photos are resized and re-encoded first (no Exif
 * leaves the device), every file is checked against its kind's cap before a byte is sent, then
 * begin → PUT with progress → complete. `attachmentIds` are the completed ones, ready to send.
 */
export const useAttachmentUpload = (channel: MessagesChannel, threadId: string) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const [items, setItems] = useState<PendingAttachment[]>([]);

  const patch = (localId: string, change: Partial<PendingAttachment>) =>
    setItems((current) => current.map((item) => (item.localId === localId ? { ...item, ...change } : item)));
  const remove = useCallback(
    (localId: string) => setItems((current) => current.filter((item) => item.localId !== localId)),
    [],
  );

  const reportFailure = (error: unknown) => {
    const quota = error as AttachmentError | undefined;
    if (quota?.code === "videoQuota" && quota.resetsOn) {
      const date = new Intl.DateTimeFormat(i18n.language, { dateStyle: "long", timeZone: "UTC" });
      toast.error(t("messages.attach.quota", { date: date.format(new Date(`${quota.resetsOn}T00:00:00Z`)) }));
    } else {
      toast.error(t("messages.attach.failed"));
    }
  };

  const upload = async (file: File): Promise<void> => {
    const declared = contentTypeOf(file);
    const isPhoto = declared.startsWith("image/");
    const localId = `local-${++nextLocalId}`;
    try {
      const image = isPhoto ? await prepareImage(file) : null;
      const blob = image?.blob ?? file;
      const contentType = image ? blob.type : declared;
      const kind = kindOf(contentType);
      if (!kind) throw new Error("Unsupported type");
      if (blob.size > maxBytesOf(kind)) {
        toast.error(t("messages.attach.tooLarge", { name: file.name, max: formatBytes(maxBytesOf(kind), i18n.language) }));
        return;
      }

      const fileName = image ? preparedFileName(file.name, contentType) : file.name;
      setItems((current) => [...current, { localId, fileName, progress: 0, attachment: null }]);
      const request = { fileName, contentType, sizeBytes: blob.size, width: image?.width, height: image?.height };
      const begun = await dispatch(beginAttachment({ channel, threadId, request })).unwrap();
      await putWithProgress(begun.uploadUrl, blob, contentType, (progress) => patch(localId, { progress }));
      const attachment = await dispatch(
        completeAttachment({ channel, threadId, attachmentId: begun.attachmentId }),
      ).unwrap();
      patch(localId, { progress: 1, attachment });
    } catch (error: unknown) {
      remove(localId);
      reportFailure(error);
    }
  };

  const addCompleted = (attachment: MessageAttachment) =>
    setItems((current) => [
      ...current,
      { localId: `local-${++nextLocalId}`, fileName: attachment.fileName, progress: 1, attachment },
    ]);

  const clear = () => setItems([]);
  const attachmentIds = items.flatMap((item) => (item.attachment ? [item.attachment.id] : []));
  const uploading = items.some((item) => item.attachment === null);

  return { items, upload, addCompleted, remove, clear, attachmentIds, uploading };
};
